import { defineStore } from "pinia";
import { computed, ref, shallowRef } from "vue";
import { LOCKABLE_RESOURCE_TYPE } from "@plugins/common/service/lock.constant";
import LockService from "@plugins/common/service/lock.service";
import { ObjectLockModel } from "@plugins/common/types.ts";
import { useErrorHandler } from "@plugins/common/composables/error.ts";

interface InitializeLockServicePayload {
  lessonUuid: string;
  activityId: string | null;
  failHandler: (response?: unknown) => void;
}

export const useLessonLockStore = defineStore("lessonLock", () => {
  // State
  const { passiveApiFailHandler } = useErrorHandler();

  const lockService = shallowRef<LockService | null>(null);

  const lockAlreadyAcquired = ref(false);

  // Getters
  const getLockService = computed<LockService | null>(() => lockService.value);

  // Methods
  const createLockService = (
    lessonUuid: string,
    activityId: string | null,
    failHandler: (response?: ObjectLockModel) => void,
  ): LockService => {
    return new LockService(
      activityId ? LOCKABLE_RESOURCE_TYPE.LESSON_ACTIVITY : LOCKABLE_RESOURCE_TYPE.LESSON,
      activityId ?? lessonUuid,
      failHandler,
      undefined,
      lessonUuid,
    );
  };

  const initLockService = (payload: InitializeLockServicePayload): void => {
    clearLockService();
    lockService.value = createLockService(
      payload.lessonUuid,
      payload.activityId,
      payload.failHandler,
    );
  };

  const unlockResource = (): void => {
    stopLockInterval();
    lockService.value?.unlock();
    lockAlreadyAcquired.value = false;
  };

  const clearLockService = (): void => {
    stopLockInterval();
    lockAlreadyAcquired.value = false;
    lockService.value = null;
  };

  const lockOnce = async (overrideExistingLock = false): Promise<void> => {
    await lockService.value?.lockOnce(overrideExistingLock);
    lockAlreadyAcquired.value = true;
  };

  const startLockInterval = async (overrideExistingLock = false): Promise<void> => {
    await lockService.value?.startLockInterval(overrideExistingLock, lockAlreadyAcquired.value);
    lockAlreadyAcquired.value = true;
  };

  const stopLockInterval = (): void => {
    lockService.value?.stopLockInterval();
  };

  const checkLock = async (isEditFlow = true): Promise<void> => {
    await lockService.value!.checkLock(isEditFlow);
  };

  const transferActivityLock = async (
    lessonUuid: string,
    activityId: string,
    failHandler: (response?: ObjectLockModel) => void,
  ): Promise<boolean | null> => {
    const previousLockService = lockService.value;

    const nextLockService = createLockService(lessonUuid, activityId, () => {
      // failure handled in the catch block.
    });

    try {
      await nextLockService.lockOnce(false);
    } catch {
      nextLockService.destroy();

      if (
        error &&
        typeof error === "object" &&
        "missingBrowserTabUuid" in error &&
        error.missingBrowserTabUuid
      ) {
        passiveApiFailHandler(error);
        return null;
      }

      return false;
    }

    // lock acquired
    // stop renewal and release previous activity lock
    previousLockService?.stopLockInterval();
    nextLockService.failHandler = failHandler;
    lockService.value = nextLockService;
    lockAlreadyAcquired.value = true;
    await nextLockService.startLockInterval(false, true);

    if (previousLockService) {
      try {
        await previousLockService.unlock();
      } catch (error) {
        passiveApiFailHandler(error);
      }
    }

    return true;
  };

  return {
    getLockService,
    initLockService,
    unlockResource,
    clearLockService,
    lockOnce,
    startLockInterval,
    stopLockInterval,
    checkLock,
    transferActivityLock,
  };
});
